/**
 * Pièces jointes d'un PDF : collecte (thread Worker, pdf.js) puis sélection de
 * la facture (thread principal, pur).
 *
 * Pourquoi cette séparation : DOMParser n'existe PAS dans un Worker. pdf.js
 * tourne donc dans le Worker (parcours des pièces jointes + métadonnées XMP),
 * et le XML est interprété sur le thread principal avec le DOMParser natif.
 */
import type { ExtractionWarning, PdfMeta, WarningSeverity } from '../model';
import { sniffXmlSyntax } from '../xml';
import { parseXmp, type XmpInfo } from './xmp';
import { PdfEncryptedError, readAttachmentsFromPdf, type RawPdfAttachment } from './raw-pdf';

export interface PdfAttachmentMeta {
  filename: string;
  content?: Uint8Array | null;
  description?: string;
}

/** Sous-ensemble de PDFDocumentProxy réellement utilisé. */
export interface PdfDocLike {
  /** pdf.js renvoie une Map (sérialisée en objet quand un Worker est utilisé). */
  getAttachments(): Promise<Map<string, PdfAttachmentMeta> | Record<string, PdfAttachmentMeta> | null>;
  /** pdf.js ≥ 5 : le contenu n'est plus fourni par getAttachments(), il faut le demander. */
  getAttachmentContent?(id: string): Promise<Uint8Array | null>;
  getData(): Promise<Uint8Array>;
  getMetadata(): Promise<{ info?: Record<string, unknown> | null; metadata?: { get(name: string): unknown } | null }>;
}

export interface PdfAttachment {
  filename: string;
  bytes: Uint8Array;
  subtype: string | null;
  source: 'name-tree' | 'af' | 'scan';
}

export interface XmlCandidate {
  attachment: PdfAttachment;
  syntax: 'CII' | 'UBL' | 'ORDER' | 'OTHER' | 'UNKNOWN';
  score: number;
}

const CANONICAL_NAMES = new Set(['factur-x.xml', 'zugferd-invoice.xml', 'xrechnung.xml', 'cii.xml']);

export function scoreAttachment(att: PdfAttachment): XmlCandidate {
  const name = att.filename.toLowerCase();
  const syntax = sniffXmlSyntax(att.bytes);
  let score = 0;
  if (name.endsWith('.xml')) score += 40;
  if (CANONICAL_NAMES.has(name)) score += 50;
  if (name.includes('factur') || name.includes('zugferd') || name.includes('xrechnung')) score += 10;
  if (syntax === 'CII' || syntax === 'UBL') score += 40;
  else if (syntax === 'ORDER') score -= 100;
  else score -= 20;
  if (att.subtype && /xml/i.test(att.subtype)) score += 10;
  return { attachment: att, syntax, score };
}

export async function toPdfAttachments(
  source: Map<string, PdfAttachmentMeta> | Record<string, PdfAttachmentMeta> | null,
  getContent?: (id: string) => Promise<Uint8Array | null>,
): Promise<PdfAttachment[]> {
  if (!source) return [];
  const entries: Array<[string, PdfAttachmentMeta]> =
    source instanceof Map ? [...source.entries()] : Object.entries(source);
  const out: PdfAttachment[] = [];
  const seen = new Set<string>();
  for (const [key, meta] of entries) {
    let bytes: Uint8Array | null | undefined = meta.content;
    if (!bytes && getContent) {
      try {
        bytes = await getContent(key);
      } catch {
        bytes = null;
      }
    }
    if (!bytes || bytes.length === 0) continue;
    const filename = meta.filename || key || 'attachment';
    const dedupe = `${filename}:${bytes.length}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({ filename, bytes, subtype: null, source: 'name-tree' });
  }
  return out;
}

export const EMPTY_XMP: XmpInfo = {
  profileLevel: null,
  documentFileName: null,
  version: null,
  documentType: null,
  pdfaPart: null,
  pdfaConformance: null,
};

export interface CollectedAttachments {
  attachments: PdfAttachment[];
  meta: PdfMeta;
  warnings: ExtractionWarning[];
}

/**
 * Étape 1 (Worker) : énumère les pièces jointes en passant par pdf.js, avec
 * repli /AF si l'arbre de noms est vide.
 */
export async function collectPdfAttachments(pdf: PdfDocLike): Promise<CollectedAttachments> {
  const warnings: ExtractionWarning[] = [];
  const warn = (code: string, message: string, severity: WarningSeverity = 'warn'): void => {
    warnings.push({ code, message, severity });
  };

  let attachments: PdfAttachment[] = [];
  let xmp: XmpInfo = { ...EMPTY_XMP };
  let producer: string | null = null;

  try {
    const record = await pdf.getAttachments();
    const getContent = pdf.getAttachmentContent?.bind(pdf);
    attachments = await toPdfAttachments(record, getContent);
  } catch {
    attachments = [];
  }

  try {
    const metadata = await pdf.getMetadata();
    const info = metadata?.info ?? null;
    if (info && typeof info.Producer === 'string') producer = info.Producer;
    const xmpRaw = metadata?.metadata?.get('xmp');
    xmp = parseXmp(
      typeof xmpRaw === 'string'
        ? xmpRaw
        : xmpRaw instanceof Uint8Array
          ? new TextDecoder('utf-8').decode(xmpRaw)
          : null,
    );
  } catch {
    /* métadonnées absentes : non bloquant */
  }

  if (!attachments.some((a) => scoreAttachment(a).score > 0)) {
    // Repli /AF : arbre de noms vide, ou XML absent de l'arbre.
    try {
      const raw = await pdf.getData();
      const afAttachments = await readAttachmentsFromPdf(raw);
      const seen = new Set(attachments.map((a) => `${a.filename}:${a.bytes.length}`));
      for (const item of afAttachments as RawPdfAttachment[]) {
        const key = `${item.filename}:${item.bytes.length}`;
        if (seen.has(key)) continue;
        seen.add(key);
        attachments.push({ filename: item.filename, bytes: item.bytes, subtype: item.subtype, source: item.source });
      }
    } catch (err) {
      if (err instanceof PdfEncryptedError) {
        warn('ENCRYPTED_PDF', 'PDF protégé par mot de passe : pièces jointes illisibles', 'error');
      } else {
        warn('NO_AF_ATTACHMENT', `Repli /AF impossible : ${err instanceof Error ? err.message : 'erreur inconnue'}`, 'info');
      }
    }
  }

  const meta: PdfMeta = {
    attachmentCount: attachments.length,
    attachmentNames: attachments.map((a) => a.filename),
    xmlSource: null,
    xmlFilename: null,
    xmpProfileLevel: xmp.profileLevel,
    xmpDocumentFileName: xmp.documentFileName,
    xmpVersion: xmp.version,
    pdfaPart: xmp.pdfaPart,
    pdfaConformance: xmp.pdfaConformance,
    producer,
  };

  return { attachments, meta, warnings };
}

export interface XmlSelection {
  chosen: PdfAttachment | null;
  candidates: XmlCandidate[];
  warnings: ExtractionWarning[];
}

/** Étape 2 (thread principal) : choix du XML facture parmi les pièces jointes. */
export function pickInvoiceXml(attachments: PdfAttachment[]): XmlSelection {
  const warnings: ExtractionWarning[] = [];
  const candidates = attachments.map(scoreAttachment).sort((a, b) => b.score - a.score);
  const positives = candidates.filter((c) => c.score > 0);
  const chosen = positives[0]?.attachment ?? null;

  if (attachments.length === 0) {
    warnings.push({ code: 'NO_AF_ATTACHMENT', severity: 'error', message: 'Aucune pièce jointe dans le PDF' });
  } else if (!chosen) {
    warnings.push({
      code: 'NO_XML_ATTACHMENT',
      severity: 'error',
      message: 'Pièces jointes présentes mais aucune facture XML reconnue',
    });
  } else if (positives.length > 1) {
    warnings.push({
      code: 'MULTIPLE_XML',
      severity: 'warn',
      message: `${positives.length} XML candidats : ${chosen.filename} retenu`,
    });
  }
  return { chosen, candidates, warnings };
}
